import React, { useState, useEffect, useCallback } from 'react';
import {
    Box, Typography, useTheme, Button, IconButton, Tabs, Tab,
    Chip, CircularProgress, Dialog, DialogTitle, DialogContent,
    DialogActions, TextField, Autocomplete, Collapse,
} from "@mui/material";
import { tokens } from "../../theme";
import Header from "../../components/Header";
import { useNavigate } from "react-router-dom";
import api from '../../utils/v2Api';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

const SUBJECTS = ['Math', 'Science', 'English', 'History', 'Economics'];

const TagManager = () => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const navigate = useNavigate();

    const [activeSubject, setActiveSubject] = useState(0);
    const [tags, setTags] = useState([]);
    const [flatTags, setFlatTags] = useState([]);
    const [stats, setStats] = useState({});
    const [loading, setLoading] = useState(true);
    const [addOpen, setAddOpen] = useState(false);
    const [editTag, setEditTag] = useState(null);
    const [deleteTag, setDeleteTag] = useState(null);

    const subject = SUBJECTS[activeSubject];

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const [treeRes, statsRes, flatRes] = await Promise.all([
                api.get('/tags', { subject }),
                api.get('/tags/stats'),
                api.get('/tags/flat', { subject }),
            ]);
            setTags(treeRes.data.data);
            setFlatTags(flatRes.data.data);

            const statsMap = {};
            (statsRes.data.data || []).forEach((s) => {
                statsMap[s.tag_id] = s;
            });
            setStats(statsMap);
        } catch (err) {
            console.error('Failed to fetch tags:', err);
        }
        setLoading(false);
    }, [subject]);

    useEffect(() => { fetchData(); }, [fetchData]);

    const handleSave = async (data) => {
        try {
            if (data.id) {
                await api.put(`/tags/${data.id}`, data);
            } else {
                await api.post('/tags', { ...data, subject });
            }
            setAddOpen(false);
            setEditTag(null);
            fetchData();
        } catch (err) {
            console.error('Failed to save tag:', err);
            alert(err.response?.data?.message || 'Failed to save tag');
        }
    };

    const handleDelete = async () => {
        if (!deleteTag) return;
        try {
            await api.delete(`/tags/${deleteTag.id}`);
            setDeleteTag(null);
            fetchData();
        } catch (err) {
            console.error('Failed to delete tag:', err);
        }
    };

    return (
        <Box m="20px">
            <Box display="flex" justifyContent="space-between" alignItems="center">
                <Header title="Tag Taxonomy" subtitle={`${flatTags.length} tags in ${subject}`} />
                <Box display="flex" gap={1}>
                    <Button
                        variant="contained"
                        color="secondary"
                        startIcon={<AddIcon />}
                        onClick={() => setAddOpen(true)}
                    >
                        Add Tag
                    </Button>
                    <Button
                        variant="outlined"
                        startIcon={<ArrowBackIcon />}
                        onClick={() => navigate('/admin/tests-v2')}
                    >
                        Test Library
                    </Button>
                </Box>
            </Box>

            <Tabs
                value={activeSubject}
                onChange={(_, v) => setActiveSubject(v)}
                sx={{ mb: 3 }}
                textColor="primary"
                indicatorColor="primary"
            >
                {SUBJECTS.map((s) => (
                    <Tab key={s} label={s} />
                ))}
            </Tabs>

            {loading ? (
                <Box display="flex" justifyContent="center" py={8}>
                    <CircularProgress />
                </Box>
            ) : tags.length === 0 ? (
                <Box display="flex" justifyContent="center" py={8}>
                    <Typography color={colors.grey[400]}>No tags in {subject}</Typography>
                </Box>
            ) : (
                <Box>
                    {tags.map((cat) => (
                        <TagTreeItem
                            key={cat.id}
                            tag={cat}
                            depth={0}
                            colors={colors}
                            stats={stats}
                            onEdit={(t) => setEditTag(t)}
                            onDelete={(t) => setDeleteTag(t)}
                        />
                    ))}
                </Box>
            )}

            {/* Add Dialog */}
            <TagDialog
                open={addOpen}
                onClose={() => setAddOpen(false)}
                onSave={handleSave}
                parentOptions={flatTags.filter((t) => t.level === 0)}
                title="Add Tag"
            />

            {/* Edit Dialog */}
            <TagDialog
                open={!!editTag}
                onClose={() => setEditTag(null)}
                onSave={handleSave}
                tag={editTag}
                parentOptions={flatTags.filter((t) => t.level === 0)}
                title="Edit Tag"
            />

            {/* Delete Confirmation */}
            <Dialog open={!!deleteTag} onClose={() => setDeleteTag(null)}>
                <DialogTitle>Delete Tag</DialogTitle>
                <DialogContent>
                    <Typography>
                        Are you sure you want to delete <strong>{deleteTag?.name}</strong>?
                        {deleteTag?.children?.length > 0 && (
                            <Typography color="error" sx={{ mt: 1 }}>
                                This will also delete {deleteTag.children.length} child tag(s) and remove all question/page associations.
                            </Typography>
                        )}
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteTag(null)}>Cancel</Button>
                    <Button onClick={handleDelete} color="error" variant="contained">Delete</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

/** Recursive tree item */
const TagTreeItem = ({ tag, depth, colors, stats, onEdit, onDelete }) => {
    const [open, setOpen] = useState(true);
    const hasChildren = tag.children && tag.children.length > 0;
    const tagStats = stats[tag.id] || { question_count: 0, page_count: 0 };

    return (
        <Box>
            <Box
                display="flex"
                alignItems="center"
                sx={{
                    py: 0.8,
                    px: 1.5,
                    ml: depth * 3,
                    borderRadius: 1,
                    '&:hover': { bgcolor: colors.primary[600] },
                    '&:hover .action-icons': { opacity: 1 },
                    cursor: hasChildren ? 'pointer' : 'default',
                }}
                onClick={() => hasChildren && setOpen(!open)}
            >
                {hasChildren ? (
                    open ? <ExpandMoreIcon fontSize="small" sx={{ mr: 1 }} /> : <ChevronRightIcon fontSize="small" sx={{ mr: 1 }} />
                ) : (
                    <Box sx={{ width: 28 }} />
                )}

                <Typography fontWeight={depth === 0 ? 700 : 400} sx={{ flex: 1 }}>
                    {tag.name}
                </Typography>

                {tag.slug && (
                    <Chip
                        label={tag.slug}
                        size="small"
                        variant="outlined"
                        sx={{ mr: 1, fontSize: '0.7rem', height: 22 }}
                    />
                )}

                <Typography variant="caption" color={colors.grey[400]} sx={{ minWidth: 50, textAlign: 'right', mr: 1 }}>
                    {tagStats.question_count > 0 ? `${tagStats.question_count} Q` : ''}
                </Typography>
                <Typography variant="caption" color={colors.grey[400]} sx={{ minWidth: 50, textAlign: 'right', mr: 1 }}>
                    {tagStats.page_count > 0 ? `${tagStats.page_count} pg` : ''}
                </Typography>

                <Box className="action-icons" sx={{ opacity: 0, transition: 'opacity 0.2s', display: 'flex' }}>
                    <IconButton size="small" onClick={(e) => { e.stopPropagation(); onEdit(tag); }}>
                        <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" onClick={(e) => { e.stopPropagation(); onDelete(tag); }}>
                        <DeleteIcon fontSize="small" />
                    </IconButton>
                </Box>
            </Box>

            {hasChildren && (
                <Collapse in={open}>
                    {tag.children
                        .sort((a, b) => a.order - b.order || a.name.localeCompare(b.name))
                        .map((child) => (
                            <TagTreeItem
                                key={child.id}
                                tag={child}
                                depth={depth + 1}
                                colors={colors}
                                stats={stats}
                                onEdit={onEdit}
                                onDelete={onDelete}
                            />
                        ))}
                </Collapse>
            )}
        </Box>
    );
};

/** Add/Edit tag dialog */
const TagDialog = ({ open, onClose, onSave, tag, parentOptions = [], title }) => {
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [parentId, setParentId] = useState(null);
    const [order, setOrder] = useState(0);

    useEffect(() => {
        if (tag) {
            setName(tag.name || '');
            setDescription(tag.description || '');
            setParentId(tag.parent_id || null);
            setOrder(tag.order || 0);
        } else {
            setName('');
            setDescription('');
            setParentId(null);
            setOrder(0);
        }
    }, [tag, open]);

    const handleSubmit = () => {
        if (!name.trim()) return;
        onSave({
            ...(tag?.id ? { id: tag.id } : {}),
            name: name.trim(),
            description: description.trim() || null,
            parent_id: parentId,
            level: parentId ? 1 : 0,
            order,
        });
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>{title}</DialogTitle>
            <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '8px !important' }}>
                <TextField
                    label="Name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    fullWidth
                    autoFocus
                />
                <Autocomplete
                    options={parentOptions}
                    getOptionLabel={(opt) => opt.name || ''}
                    value={parentOptions.find((p) => p.id === parentId) || null}
                    onChange={(_, val) => setParentId(val?.id || null)}
                    renderInput={(params) => <TextField {...params} label="Parent Category" />}
                    isOptionEqualToValue={(opt, val) => opt.id === val.id}
                />
                <TextField
                    label="Description"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    fullWidth
                    multiline
                    rows={2}
                />
                <TextField
                    label="Order"
                    type="number"
                    value={order}
                    onChange={(e) => setOrder(parseInt(e.target.value) || 0)}
                    sx={{ maxWidth: 120 }}
                />
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button onClick={handleSubmit} variant="contained" color="secondary" disabled={!name.trim()}>
                    Save
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default TagManager;
